/**
 * The row of stat cards above a list — list engine v2, "the list is the
 * dashboard".
 *
 * The strip is the cards, and nothing else: it draws them, it lets a card be
 * clicked to filter the list, and it hands its ⋯ menu back to whoever owns the
 * builder. Deliberately not part of `ListScreen`, because the screen is
 * already the largest component in the product and a strip that knows about
 * drag order, menus and aggregates would double it.
 *
 * Reordering is native drag-and-drop *and* two menu items. The drag is what
 * people reach for; the menu items are what makes it reachable without a
 * mouse, which a strip that can only be dragged is not.
 */
import { useState } from 'react'
import { accentColor, filterKey, type ListCard } from '@rowted/shared'
import { StatCard, NewCardTile } from './stat-card.js'
import { MetricCard } from './primitives.js'
import { ContextMenu, type MenuItem } from './data-list.js'
import { describeMeasure } from './card-builder.js'
import { cn } from '../lib/cn.js'
import { formatCompact } from '../lib/format.js'
import { useStrip, type StripWidths } from '../lib/use-strip.js'
import type { UseCardsResult } from '../lib/use-cards.js'
import type { ListMetric } from './list-screen.js'
import type { UseListResult } from '../lib/use-list.js'

export interface CardStripProps {
  list: UseListResult
  /** What the list is called, for the words under a card. */
  listTitle: string
  cards: UseCardsResult
  /**
   * The screen's own fixed metrics, which come first. They are in here rather
   * than beside it because the row has to be measured as one thing: two
   * components each fitting themselves is how you get one of them wrapping.
   */
  metrics?: ListMetric[]
  /** Whether a metric is currently the list's filter, and what clicking does. */
  metricSelected?: (metric: ListMetric) => boolean
  onMetricClick?: (metric: ListMetric) => void
  /** Opens the builder — with a card to change, or null for a new one. */
  onEdit: (card: ListCard | null) => void
  /** Cards are off on screens that do not have them; metrics may still be on. */
  cardsOn?: boolean
}

/*
 * What each kind of thing needs, at each density.
 *
 * Hand-written rather than measured, and they are minimums: the cards flex to
 * fill whatever is left over, so these decide *when* a row gives way, not how
 * wide anything ends up. A card with a chart needs room for the chart beside
 * the figure; one without is a word and a number.
 */
const PICTURE_CARD: StripWidths = { full: 300, compact: 232, flat: 164, chip: 128 }
const PLAIN_CARD: StripWidths = { full: 190, compact: 168, flat: 152, chip: 128 }
const METRIC: StripWidths = { full: 172, compact: 156, flat: 148, chip: 128 }
/*
 * The tile's `chip` width is the square it actually draws — the same 36px
 * square the toolbar's buttons are. It said 44, and a strip that budgets more
 * than it spends is a strip with a gap at the end of it.
 */
const NEW_TILE: StripWidths = { full: 148, compact: 104, flat: 104, chip: 36 }

export function CardStrip({
  list, listTitle, cards, metrics = [], metricSelected, onMetricClick, onEdit, cardsOn = true,
}: CardStripProps) {
  const [menu, setMenu] = useState<{ card: ListCard; x: number; y: number } | null>(null)
  const [overflow, setOverflow] = useState<{ x: number; y: number } | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)
  const [over, setOver] = useState<string | null>(null)

  const viewKey = filterKey(list.view.filter?.conditions ?? [])

  /*
   * A card is "on" when the list is filtered exactly the way the card is. A
   * card with no filter of its own is not selectable: it would light up on
   * every unfiltered list, and clicking it would do nothing visible.
   */
  const selectable = (card: ListCard): boolean =>
    (card.definition.filter?.conditions.length ?? 0) > 0

  const active = (card: ListCard): boolean =>
    selectable(card) && viewKey === filterKey(card.definition.filter!.conditions)

  const toggle = (card: ListCard): void => {
    if (!selectable(card)) return
    list.setFilter(active(card) ? [] : card.definition.filter!.conditions)
  }

  const menuItems = (card: ListCard): MenuItem[] => {
    const index = cards.cards.findIndex((entry) => entry.id === card.id)
    const items: MenuItem[] = []

    if (card.editable) {
      items.push({ key: 'edit', label: 'Edit card', tone: 'primary', onSelect: () => onEdit(card) })
      items.push({
        key: 'duplicate',
        label: 'Duplicate',
        onSelect: () => void cards.create({
          definition: { ...card.definition, label: `${card.definition.label} copy` },
          audience: card.audience,
          orgUnitId: card.orgUnitId,
        }),
      })
    }

    if (index > 0) {
      items.push({
        key: 'left', label: 'Move left', separatorBefore: card.editable,
        onSelect: () => void cards.move(card.id, index - 1),
      })
    }
    if (index < cards.cards.length - 1) {
      items.push({
        key: 'right', label: 'Move right',
        onSelect: () => void cards.move(card.id, index + 1),
      })
    }

    if (card.editable) {
      items.push({
        key: 'remove', label: 'Remove card', tone: 'danger', separatorBefore: true,
        onSelect: () => void cards.remove(card.id),
      })
    }

    return items
  }

  const drop = (target: ListCard): void => {
    setOver(null)
    if (!dragging || dragging === target.id) return
    const to = cards.cards.findIndex((entry) => entry.id === target.id)
    if (to >= 0) void cards.move(dragging, to)
    setDragging(null)
  }

  /** What the card's number is, in words, under the number. */
  const footnote = (card: ListCard): string => describeMeasure(
    card.definition,
    list.fields.find((field) => field.name === card.definition.measure.field),
    listTitle)

  /*
   * The row, measured as one thing.
   *
   * Metrics first because they are what the module is about, then the cards
   * people added, then the tile. `visible` counts across both: when the row
   * runs out of space it sheds from the right-hand end, which is the newest
   * card, not somebody's fixed metric.
   */
  const shown = cardsOn ? cards.cards : []
  const widths: StripWidths[] = [
    ...metrics.map(() => METRIC),
    ...shown.map((card) => (hasPicture(card) ? PICTURE_CARD : PLAIN_CARD)),
  ]
  const { ref, density, visible } = useStrip(widths, cardsOn ? NEW_TILE : ZERO_TILE)

  const metricCount = Math.min(metrics.length, visible)
  const cardCount = Math.max(0, visible - metrics.length)
  const dropped = widths.length - visible

  return (
    /*
      * **Room for the ring** (Aaron: the highlight "is bigger on left and
      * right than top and bottom and looks uneven").
      *
      * A `ring` is painted outside the border box, and this strip is
      * `overflow-hidden` — it has to be, because that is what hides the cards
      * that did not fit so the "+N" can offer them. Horizontally the gap and
      * the page padding gave the ring somewhere to go; vertically the box was
      * exactly the cards' height, so the 2px of a selected ring and the 1px
      * of a hover one were sliced off top and bottom. The ring was there all
      * round; two of its sides were simply outside the box.
      *
      * `py-1` gives it 4px of room and `-my-1` takes the same back off the
      * outside, so the strip occupies exactly the height it did before and
      * nothing below it moves.
      */
    <div ref={ref} className="-my-1 flex items-stretch gap-2 overflow-hidden py-1">
      {metrics.slice(0, metricCount).map((metric) => (
        <div
          key={metric.key}
          className="flex min-w-0 flex-1"
          style={{ minWidth: METRIC[density], maxWidth: density === 'full' ? 260 : 220 }}
        >
          <MetricCard
            label={metric.label}
            value={metric.value}
            sub={metric.sub}
            dotColor={metric.dotColor}
            density={density}
            selected={metricSelected?.(metric) ?? false}
            onClick={onMetricClick ? () => onMetricClick(metric) : undefined}
          />
        </div>
      ))}

      {shown.slice(0, cardCount).map((card) => (
        <div
          key={card.id}
          data-card={card.id}
          draggable
          onDragStart={(event) => {
            setDragging(card.id)
            event.dataTransfer.effectAllowed = 'move'
            // Firefox refuses to start a drag without payload on the event.
            event.dataTransfer.setData('text/plain', card.id)
          }}
          onDragEnd={() => { setDragging(null); setOver(null) }}
          onDragOver={(event) => {
            if (!dragging) return
            event.preventDefault()
            setOver(card.id)
          }}
          onDragLeave={() => setOver((current) => (current === card.id ? null : current))}
          onDrop={(event) => { event.preventDefault(); drop(card) }}
          style={{
            /*
             * How wide depends on what is in it *and* on how much room the row
             * has. A trend needs space for its picture beside the figure; a
             * plain count is a word and a number. `flex-1` then shares out
             * whatever is left, so a strip of two does not leave half the page
             * empty.
             */
            minWidth: (hasPicture(card) ? PICTURE_CARD : PLAIN_CARD)[density],
            maxWidth: density === 'full' ? (hasPicture(card) ? 400 : 260) : undefined,
          }}
          className={cn(
            'flex min-w-0 flex-1',
            dragging === card.id && 'opacity-40',
            // Where it would land, as a green edge rather than a moving card.
            over === card.id && dragging !== card.id && 'rounded-card ring-2 ring-accent-line',
          )}
        >
          <StatCard
            definition={card.definition}
            result={cards.results[card.id] ?? null}
            unit={unitOf(card, list)}
            footnote={footnote(card)}
            loading={cards.results[card.id] === undefined}
            dotColor={accentColor(card.definition.accent)}
            density={density}
            selected={active(card)}
            onClick={selectable(card) ? () => toggle(card) : undefined}
            onMenu={(x, y) => setMenu({ card, x, y })}
          />
        </div>
      ))}

      {/*
        * What the row could not fit — and a way to it.
        *
        * It started as a count, which was worse than nothing: adding a card on
        * a narrow window put it straight into the overflow, so the thing you
        * had just made was a grey "+1" you could not open. It is a button now,
        * and its menu is the cards themselves — figure included, and clicking
        * one filters the list exactly as clicking the card would.
        */}
      {dropped > 0 && (
        <button
          type="button"
          aria-label={`${dropped} more ${dropped === 1 ? 'card' : 'cards'}`}
          title={`${dropped} more ${dropped === 1 ? 'card' : 'cards'}`}
          onClick={(event) => {
            const box = event.currentTarget.getBoundingClientRect()
            setOverflow({ x: box.left, y: box.bottom + 4 })
          }}
          className={cn(
            'flex shrink-0 items-center justify-center whitespace-nowrap rounded-card',
            'border border-line bg-surface text-sub text-text-muted',
            'hover:bg-hover hover:text-text',
            /*
             * The toolbar's width, the row's height.
             *
             * 36px wide, so it sits in the same column as the buttons under
             * it, and as tall as the cards beside it, which the row's
             * `items-stretch` gives it for nothing. Square and centred, it
             * left a gap above and below that read as a card that had not
             * loaded.
             */
            density === 'chip' ? 'w-9 px-0' : 'px-2',
          )}
        >
          +{dropped}
        </button>
      )}

      {cardsOn && <NewCardTile onClick={() => onEdit(null)} density={density} />}

      {menu && (
        <ContextMenu
          items={menuItems(menu.card)}
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
        />
      )}

      {overflow && (
        <ContextMenu
          x={overflow.x}
          y={overflow.y}
          onClose={() => setOverflow(null)}
          items={[
            ...metrics.slice(metricCount).map((metric) => ({
              key: `m:${metric.key}`,
              label: `${metric.label} · ${metric.value}`,
              onSelect: () => { if (metric.filter) onMetricClick?.(metric) },
            })),
            ...shown.slice(cardCount).map((card) => ({
              key: card.id,
              label: `${card.definition.label} · ${figureOf(card, cards, list)}`,
              onSelect: () => toggle(card),
            })),
          ]}
        />
      )}
    </div>
  )
}

/** A dropped card's number, for the line that stands in for it. */
function figureOf(card: ListCard, cards: UseCardsResult, list: UseListResult): string {
  const result = cards.results[card.id]
  return result ? formatCompact(result.value, unitOf(card, list)) : '—'
}

/** A row with no cards has no tile to leave room for. */
const ZERO_TILE: StripWidths = { full: 0, compact: 0, flat: 0, chip: 0 }

/** Whether the card draws a chart beside its number, which decides its width. */
function hasPicture(card: ListCard): boolean {
  const { display, compare } = card.definition
  return display !== 'number' || compare.kind !== 'none'
}

/** Money wears a `$`; everything else does not. */
function unitOf(card: ListCard, list: UseListResult): 'money' | 'number' {
  if (card.definition.measure.fn === 'count') return 'number'
  const field = list.fields.find((entry) => entry.name === card.definition.measure.field)
  return field?.kind === 'money' ? 'money' : 'number'
}
